<template>
  <div v-if="showModal" class="modal-overlay" @click="closeModal">
    <div class="modal-card" @click.stop>
      <!-- Header -->
      <div class="modal-header">
        <div class="header-left">
          <div class="header-icon">
            <i class="fa fa-cubes"></i>
          </div>
          <div>
            <h4 class="modal-title">Stock Allocation Details</h4>
            <span v-if="stockAllocation.stock_allocations_no" class="allocation-no-badge">
              {{ stockAllocation.stock_allocations_no }}
            </span>
          </div>
        </div>
        <button type="button" class="close-btn" @click="closeModal" title="Close">&times;</button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <i class="fa fa-spinner fa-spin"></i>
        <span>Loading details...</span>
      </div>

      <!-- Content State -->
      <div v-else class="modal-body">
        <!-- Info Grid -->
        <div class="info-grid">
          <!-- Allocation No -->
          <div class="info-item">
            <span class="info-label">Stock Allocation No</span>
            <span class="info-value font-weight-bold">{{ stockAllocation.stock_allocations_no || '-' }}</span>
          </div>

          <!-- Type Badge -->
          <div class="info-item">
            <span class="info-label">Type</span>
            <div>
              <span :class="getTypeLabelClass(stockAllocation.is_temp)">
                {{ getTypeLabel(stockAllocation.is_temp) }}
              </span>
            </div>
          </div>

          <!-- Status Badge -->
          <div class="info-item">
            <span class="info-label">Status</span>
            <div>
              <span :class="getStatusLabelClass(stockAllocation)">
                {{ getDispatchStatus(stockAllocation) }}
              </span>
            </div>
          </div>

          <!-- Sales Rep (Vehicle) -->
          <div class="info-item">
            <span class="info-label">Sales Rep (Vehicle)</span>
            <span class="info-value">{{ getDsrOrDealerName(stockAllocation) }}</span>
          </div>

          <!-- Created Datetime -->
          <div class="info-item">
            <span class="info-label">Created At</span>
            <span class="info-value">{{ formatDate(stockAllocation.created_at) }}</span>
          </div>

          <!-- Dispatched / Confirmed Datetime -->
          <div class="info-item">
            <span class="info-label">Dispatched / Confirmed At</span>
            <span class="info-value">{{ getDispatchOrConfirmedDate(stockAllocation) }}</span>
          </div>
        </div>

        <!-- Items Table -->
        <div class="items-section">
          <h5 class="items-title">Allocated Items</h5>
          <div class="items-table-container">
            <table class="table modal-items-table">
              <thead>
                <tr>
                  <th style="width: 50px;">#</th>
                  <th>Item Name</th>
                  <th class="text-right" style="width: 120px;">Quantity</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in items" :key="index">
                  <td class="index-col">{{ index + 1 }}</td>
                  <td class="item-name-cell">{{ item.item_name }}</td>
                  <td class="text-right qty-cell">{{ parseQty(item.qty) }}</td>
                </tr>
                <tr v-if="!items || items.length === 0">
                  <td colspan="3" class="text-center py-3 text-muted">No items found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary close-footer-btn" @click="closeModal">Close</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { fetchStockAllocationPopup } from '@/services/dataService';

const props = defineProps({
  showModal: {
    type: Boolean,
    default: false
  },
  allocationId: {
    type: [String, Number],
    default: null
  }
});

const emit = defineEmits(['close']);

const stockAllocation = ref({});
const items = ref([]);
const loading = ref(false);
const token = ref(localStorage.getItem('token_set') || '');

const closeModal = () => {
  emit('close');
};

const loadDetails = async (id) => {
  if (!id) return;
  loading.value = true;
  try {
    const response = await fetchStockAllocationPopup(id, token.value);
    if (response?.data?.status === 'success' || response?.data?.data) {
      stockAllocation.value = response.data.data.stock_allocation || {};
      items.value = response.data.data.items || [];
    }
  } catch (error) {
    console.error("Error fetching stock allocation popup details:", error);
  } finally {
    loading.value = false;
  }
};

watch(() => props.showModal, (newVal) => {
  if (newVal && props.allocationId) {
    loadDetails(props.allocationId);
  }
}, { immediate: true });

watch(() => props.allocationId, (newId) => {
  if (props.showModal && newId) {
    loadDetails(newId);
  }
});

const getTypeLabel = (isTemp) => {
  return (isTemp === '1' || isTemp === 1) ? 'Temporary' : 'Original';
};

const getTypeLabelClass = (isTemp) => {
  return (isTemp === '1' || isTemp === 1) ? 'badge-type type-temp' : 'badge-type type-original';
};

const getDispatchStatus = (item) => {
  if (!item) return 'Pending';
  const isTemp = item.is_temp === '1' || item.is_temp === 1;
  const isDispatched = item.is_dispatched === '1' || item.is_dispatched === 1;
  const isConfirmed = item.is_sales_rep_confirmed === '1' || item.is_sales_rep_confirmed === 1;

  if (isTemp) {
    return isConfirmed ? 'DSR Accepted' : 'Pending';
  } else {
    return isDispatched ? 'Dispatched' : 'Pending';
  }
};

const getStatusLabelClass = (item) => {
  if (!item) return 'badge-status badge-pending';
  const isTemp = item.is_temp === '1' || item.is_temp === 1;
  const isDispatched = item.is_dispatched === '1' || item.is_dispatched === 1;
  const isConfirmed = item.is_sales_rep_confirmed === '1' || item.is_sales_rep_confirmed === 1;

  if (isTemp) {
    return isConfirmed ? 'badge-status badge-accepted' : 'badge-status badge-pending';
  } else {
    return isDispatched ? 'badge-status badge-dispatched' : 'badge-status badge-pending';
  }
};

const formatDate = (dateStr) => {
  if (!dateStr || dateStr.startsWith('1900-01-01') || dateStr.startsWith('0000-00-00')) {
    return '-';
  }
  const formattedStr = dateStr.includes(' ') && !dateStr.includes('T') ? dateStr.replace(' ', 'T') : dateStr;
  const date = new Date(formattedStr);
  if (isNaN(date.getTime())) {
    return dateStr;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const formattedHours = String(hours).padStart(2, '0');

  return `${year}-${month}-${day} ${formattedHours}:${minutes} ${ampm}`;
};

const getDispatchOrConfirmedDate = (item) => {
  if (!item) return '-';
  if (item.is_temp === '1' || item.is_temp === 1) {
    return formatDate(item.sales_rep_confirmed_at);
  }
  return formatDate(item.dispatch_created_at);
};

const getDsrOrDealerName = (item) => {
  if (!item) return '-';
  if (item.business_name) return item.business_name;
  const name = ((item.first_name || '') + ' ' + (item.last_name || '')).trim();
  return name || '-';
};

const parseQty = (qty) => {
  if (qty === null || qty === undefined) return 0;
  const val = parseFloat(qty);
  return isNaN(val) ? qty : val;
};
</script>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
  padding: 16px;
}

.modal-card {
  background-color: #ffffff;
  border-radius: 12px;
  width: 560px;
  max-width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: modalFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(-16px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid #f1f5f9;
  background-color: #ffffff;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background-color: #e0f2fe;
  color: #0284c7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.modal-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.allocation-no-badge {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    color: #0f172a;
    background-color: #f1f5f9;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: #0284c7;
  font-size: 15px;
  font-weight: 500;

  .fa-spin {
    font-size: 28px;
  }
}

.modal-body {
  padding: 20px 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px 20px;
  background-color: #f8fafc;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid #f1f5f9;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
}

.info-value {
  font-size: 13px;
  color: #1e293b;
  word-break: break-word;
}

.items-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.items-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #475569;
  margin: 0;
}

.items-table-container {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  max-height: 240px;
  overflow-y: auto;
}

.modal-items-table {
  width: 100%;
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;

  th {
    padding: 10px 14px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #475569;
    background-color: #f1f5f9;
    border-bottom: 1px solid #e2e8f0;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  td {
    padding: 10px 14px;
    font-size: 13px;
    color: #1e293b;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background-color: #f8fafc;
  }

  .index-col {
    color: #94a3b8;
    font-size: 12px;
  }

  .item-name-cell {
    font-weight: 500;
  }

  .qty-cell {
    font-weight: 600;
    color: #0f172a;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 14px 24px;
  border-top: 1px solid #f1f5f9;
  background-color: #f8fafc;
}

.close-footer-btn {
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  background-color: #64748b;
  color: #ffffff;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #475569;
  }
}

/* Badge Styling - Matches Table Design */
.badge-type {
  display: inline-block;
  min-width: 85px;
  width: 85px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  border-radius: 4px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.type-original {
  background-color: #e3f2fd;
  color: #0d47a1;
  border: 1px solid #bbdefb;
}

.type-temp {
  background-color: #fff3e0;
  color: #e65100;
  border: 1px solid #ffe0b2;
}

.badge-status {
  display: inline-block;
  min-width: 105px;
  width: 105px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  border-radius: 4px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: #ffffff;
}

.badge-dispatched,
.badge-accepted {
  background-color: #069e27;
  border: 1px solid #058521;
}

.badge-pending {
  background-color: #d32f2f;
  border: 1px solid #b71c1c;
}
</style>
